/**
 * Schedule Grid Tab - Interactive class scheduling interface
 *
 * Primary interface for creating and managing class schedules with
 * drag-and-drop functionality.
 *
 * Phase 1: Basic grid structure with scheduled class display
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Snackbar,
  Button,
} from '@mui/material';
import { useGetScheduledClassesQuery } from '../../../../api/schedulingApi';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import AddClassDialog from './components/AddClassDialog';
import EditScheduledClassDialog from './components/EditScheduledClassDialog';
import ScheduleGrid from '../../components/ScheduleGrid';
import type { ScheduledClass } from '../../../../types/scheduling';

interface ScheduleGridTabProps {
  scheduleId: string;
  studioId: string;
}

const ScheduleGridTab: React.FC<ScheduleGridTabProps> = ({
  scheduleId,
  studioId,
}) => {
  const {
    data: scheduledClasses = [],
    isLoading,
    error,
  } = useGetScheduledClassesQuery({ scheduleId, studioId });

  // Add class dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  // Edit class dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingScheduledClass, setEditingScheduledClass] =
    useState<ScheduledClass | null>(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Drag-and-drop hook with snackbar callbacks
  const { dragState, handleDragStart, handleDragEnd, undoLastMove, canUndo } =
    useDragAndDrop(
      scheduledClasses,
      60, // Default time increment (will match grid's current zoom level in future)
      studioId,
      message => {
        setSnackbarMessage(message);
        setSnackbarOpen(true);
      },
      message => {
        setSnackbarMessage(message);
        setSnackbarOpen(true);
      }
    );

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleUndo = () => {
    undoLastMove();
    setSnackbarOpen(false);
  };

  const handleCellClick = (day: string, time: string) => {
    setSelectedDay(day);
    setSelectedTime(time);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleClassClick = (scheduledClass: ScheduledClass) => {
    setEditingScheduledClass(scheduledClass);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingScheduledClass(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load scheduled classes. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h6" gutterBottom>
          Schedule Grid
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Click on any cell to add a class, or click on an existing class to
          edit it.
        </Typography>
      </Box>

      {/* Use shared ScheduleGrid component in editable mode */}
      <ScheduleGrid
        scheduledClasses={scheduledClasses}
        isEditable
        onCellClick={handleCellClick}
        onClassClick={handleClassClick}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        activeClassId={dragState.activeClassId}
      />

      <Alert severity="success" sx={{ mt: 2 }}>
        <Typography variant="body2" fontWeight="bold">
          Phase 1 - Edit & Delete Complete ✅
        </Typography>
        <Typography variant="caption">
          Click on any class block to edit or delete it. Coming next: Manual
          scheduling flag (backend), drag-and-drop editing, algorithm
          protection.
        </Typography>
      </Alert>

      {/* Add Class Dialog */}
      <AddClassDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        scheduleId={scheduleId}
        studioId={studioId}
        prefilledDay={selectedDay}
        prefilledStartTime={selectedTime}
      />

      {/* Edit Class Dialog */}
      <EditScheduledClassDialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        scheduledClass={editingScheduledClass}
        studioId={studioId}
      />

      {/* Snackbar for drag-and-drop feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        action={
          canUndo && (
            <Button color="secondary" size="small" onClick={handleUndo}>
              UNDO
            </Button>
          )
        }
      />
    </Box>
  );
};

export default ScheduleGridTab;
